import React from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Box, TextField, MenuItem, Stack } from "@mui/material";

export interface FilterConfig {
  label: string;
  field: string;
  options: Array<{ value: string; label: string }>;
}

interface FilterableDataGridProps<T> {
  rows: T[];
  columns: GridColDef[];
  filters?: FilterConfig[];
  searchPlaceholder?: string;
  searchFields?: string[];
  height?: number;
  pageSize?: number;
}

export function FilterableDataGrid<T extends { id: string | number }>({
  rows,
  columns,
  filters = [],
  searchPlaceholder = "Search",
  searchFields = [],
  height = 600,
  pageSize = 10,
}: FilterableDataGridProps<T>) {
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [filterValues, setFilterValues] = React.useState<
    Record<string, string>
  >(filters.reduce((acc, filter) => ({ ...acc, [filter.field]: "ALL" }), {}));
  const [currentPaginationModel, setCurrentPaginationModel] = React.useState({
    page: 0,
    pageSize,
  });

  const filteredRows = React.useMemo(() => {
    return rows.filter((row) => {
      // Search filter
      const matchesSearch =
        searchQuery === "" ||
        searchFields.some((field) => {
          const value = (row as any)[field];
          return (
            value &&
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
          );
        });

      // Apply all custom filters
      const matchesFilters = filters.every((filter) => {
        const filterValue = filterValues[filter.field];
        if (filterValue === "ALL") return true;
        const rowValue = (row as any)[filter.field];
        return String(rowValue).toUpperCase() === filterValue;
      });

      return matchesSearch && matchesFilters;
    });
  }, [rows, searchQuery, filterValues, searchFields, filters]);

  const handleFilterChange = (field: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Box sx={{ width: "100%" }}>
      {(searchFields.length > 0 || filters.length > 0) && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2}>
            {searchFields.length > 0 && (
              <TextField
                label="Search"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
            )}
            {filters.map((filter) => (
              <TextField
                key={filter.field}
                select
                label={filter.label}
                value={filterValues[filter.field]}
                onChange={(e) =>
                  handleFilterChange(filter.field, e.target.value)
                }
                size="small"
                sx={{ minWidth: 150 }}
              >
                {filter.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ))}
          </Stack>
        </Stack>
      )}

      <Box sx={{ height, width: "100%" }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          paginationModel={currentPaginationModel}
          onPaginationModelChange={(newPaginationModel) =>
            setCurrentPaginationModel(newPaginationModel)
          }
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <strong>
          Showing {filteredRows.length} of {rows.length} items
        </strong>
      </Box>
    </Box>
  );
}
